import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { api, ApiError, getToken, setUnauthorizedHandler } from "./api";
import { rememberGoogleSignOut } from "./googleIdentity";
import { applyThemePref } from "./theme";
import type { UserResponse } from "./api";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  status: AuthStatus;
  user: UserResponse | null;
  setUser(user: UserResponse | null): void;
  login(email: string, password: string): Promise<void>;
  signInWithGoogle(credential: string): Promise<void>;
  register(email: string, password: string, displayName?: string | null): Promise<void>;
  logout(): void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserResponse | null>(null);

  // The account's theme preference wins over the localStorage mirror the moment
  // the user is known (src/lib/theme.ts; the pre-bundle index.html snippet only
  // covers the pre-auth paint).
  useEffect(() => {
    if (user?.theme) applyThemePref(user.theme);
  }, [user?.theme]);
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    // An authenticated request that 401s (expired token) drops the session here so
    // RequireAuth sends the user to /login instead of leaving them on a dead page.
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus("anonymous");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setStatus("anonymous");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const me = await api.me();
        if (cancelled) return;
        setUser(me);
        setStatus("authenticated");
      } catch (e) {
        if (cancelled) return;
        // ApiError with 401 has already cleared the token via the client.
        if (!(e instanceof ApiError)) console.error("auth bootstrap failed", e);
        setUser(null);
        setStatus("anonymous");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    const resp = await api.login({ email, password });
    setUser(resp.user);
    setStatus("authenticated");
  };

  const signInWithGoogle = async (credential: string): Promise<void> => {
    const resp = await api.googleSignIn(credential);
    setUser(resp.user);
    setStatus("authenticated");
  };

  const register = async (
    email: string,
    password: string,
    displayName?: string | null,
  ): Promise<void> => {
    await api.register({ email, password, display_name: displayName ?? null });
  };

  const logout = (): void => {
    api.logout();
    // Without this, Chrome's automatic Google sign-in would log the user straight back
    // in on the next /login visit, even after a reload (ADR 0007).
    rememberGoogleSignOut();
    setUser(null);
    setStatus("anonymous");
  };

  return (
    <AuthContext.Provider value={{ status, user, setUser, login, signInWithGoogle, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
